import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type FilterStore = {
    open: boolean;
    setOpen: (open: boolean) => void;
    activeFiltersCount: number;

    activeVersion: string | null;
    activeModalVersion: string | null;
    setActiveVersion: (version: string | null) => void;
    setActiveModalVersion: (version: string | null) => void;
    activeDisciplines: string[];
    setActiveDisciplines: (disciplines: string[]) => void;
    activeIfcVersion: string | null;
    setActiveIfcVersion: (version: string | null) => void;
    activeSoftware: string | null;
    setActiveSoftware: (software: string | null) => void;
    activeClassification: string | null;
    setActiveClassification: (classification: string | null) => void;
    activeUseCases: string[];
    setActiveUseCases: (useCases: string[]) => void;

    unconfirmedVersion: string | null;
    setUnconfirmedVersion: (version: string | null) => void;
    unconfirmedDisciplines: string[];
    setUnconfirmedDisciplines: (disciplines: string[]) => void;
    unconfirmedIfcVersion: string | null;
    setUnconfirmedIfcVersion: (version: string | null) => void;
    unconfirmedSoftware: string | null;
    setUnconfirmedSoftware: (software: string | null) => void;
    unconfirmedClassification: string | null;
    setUnconfirmedClassification: (classification: string | null) => void;
    unconfirmedUseCases: string[];
    setUnconfirmedUseCases: (useCases: string[]) => void;
};

const useFilterStore = create<FilterStore>()(
    persist(
        (set, get) => ({
            open: false,
            setOpen: (open: boolean) =>
                set({
                    open,
                    unconfirmedVersion: get().activeVersion,
                    unconfirmedDisciplines: [...get().activeDisciplines],
                    unconfirmedIfcVersion: get().activeIfcVersion,
                    unconfirmedSoftware: get().activeSoftware,
                    unconfirmedClassification: get().activeClassification,
                    unconfirmedUseCases: [...get().activeUseCases],
                }),
            activeFiltersCount: 0,
            activeVersion: null,
            activeModalVersion: null,
            activeClassification: null,
            activeDisciplines: [],
            activeIfcVersion: "",
            activeSoftware: "",
            activeUseCases: [],

            unconfirmedVersion: null,
            setUnconfirmedVersion: (version: string | null) => set({ unconfirmedVersion: version }),
            unconfirmedDisciplines: [],
            setUnconfirmedDisciplines: (disciplines: string[]) => set({ unconfirmedDisciplines: disciplines }),
            unconfirmedIfcVersion: null,
            setUnconfirmedIfcVersion: (version: string | null) => set({ unconfirmedIfcVersion: version }),
            unconfirmedSoftware: null,
            setUnconfirmedSoftware: (software: string | null) => set({ unconfirmedSoftware: software }),
            unconfirmedClassification: null,
            setUnconfirmedClassification: (classification: string | null) =>
                set({ unconfirmedClassification: classification }),
            unconfirmedUseCases: [],
            setUnconfirmedUseCases: (useCases: string[]) => set({ unconfirmedUseCases: useCases }),

            setActiveVersion: (version: string | null) => set({ activeVersion: version, activeModalVersion: version }),
            setActiveModalVersion: (version: string | null) => set({ activeModalVersion: version }),
            setActiveClassification: (classification: string | null) =>
                set({
                    activeClassification: classification,
                    activeFiltersCount:
                        1 + // There is always an active version
                        get().activeDisciplines.length +
                        (get().activeIfcVersion ? 1 : 0) +
                        (get().activeSoftware ? 1 : 0) +
                        (get().activeClassification ? 1 : 0) +
                        get().activeUseCases.length,
                }),
            setActiveDisciplines: (disciplines: string[]) =>
                set({
                    activeDisciplines: disciplines,
                    activeFiltersCount:
                        1 + // There is always an active version
                        disciplines.length +
                        (get().activeIfcVersion ? 1 : 0) +
                        (get().activeSoftware ? 1 : 0) +
                        (get().activeClassification ? 1 : 0) +
                        get().activeUseCases.length,
                }),
            setActiveIfcVersion: (version: string | null) =>
                set({
                    activeIfcVersion: version,
                    activeFiltersCount:
                        1 + // There is always an active version
                        get().activeDisciplines.length +
                        (version ? 1 : 0) +
                        (get().activeSoftware ? 1 : 0) +
                        (get().activeClassification ? 1 : 0) +
                        get().activeUseCases.length,
                }),
            setActiveSoftware: (software: string | null) =>
                set({
                    activeSoftware: software,
                    activeFiltersCount:
                        1 + // There is always an active version
                        get().activeDisciplines.length +
                        (get().activeIfcVersion ? 1 : 0) +
                        (software ? 1 : 0) +
                        (get().activeClassification ? 1 : 0) +
                        get().activeUseCases.length,
                }),
            setActiveUseCases: (useCases: string[]) =>
                set({
                    activeUseCases: useCases,
                    activeFiltersCount:
                        1 + // There is always an active version
                        get().activeDisciplines.length +
                        (get().activeIfcVersion ? 1 : 0) +
                        (get().activeSoftware ? 1 : 0) +
                        (get().activeClassification ? 1 : 0) +
                        useCases.length,
                }),
        }),
        {
            version: 3,
            name: "filter-storage",
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                activeVersion: state.activeVersion,
                activeModalVersion: state.activeModalVersion,
                activeFiltersCount: state.activeFiltersCount,
                activeClassification: state.activeClassification,
                activeDisciplines: state.activeDisciplines,
                activeIfcVersion: state.activeIfcVersion,
                activeSoftware: state.activeSoftware,
                activeUseCases: state.activeUseCases,
            }),
        }
    )
);

export default useFilterStore;
